// BrandHeader — the context bar: restaurant-specific chrome, exactly 56px (h-14,
// border included), bg-card like the sidebar. From md up it never scrolls: the
// shell's main column is a flex column and the page scrolls in the region below
// it. Below md the page scrolls with the window, so the bar stays sticky.
// Left: green two-wing Synvya mark (26px) + restaurant name + section label.
// Right: LIVE/OFFLINE bunker pill + help icon.

import { HelpCircle } from 'lucide-react'
import { SynvyaMark } from '@synvya/portal-ui/branding'

interface BrandHeaderProps {
  restaurantName: string
  sectionLabel: string
  bunkerConnected: boolean
  connectionError?: string | null
  onHelp?: () => void
}

function BunkerPill({ connected }: { connected: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
        connected
          ? 'bg-emerald-500/15 text-emerald-400'
          : 'bg-red-500/15 text-red-400'
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          connected ? 'bg-emerald-400 shadow-[0_0_6px_rgba(34,197,94,0.6)]' : 'bg-red-400 shadow-[0_0_6px_rgba(239,68,68,0.6)]'
        }`}
      />
      {connected ? 'LIVE' : 'OFFLINE'}
    </span>
  )
}

export function BrandHeader({
  restaurantName,
  sectionLabel,
  bunkerConnected,
  connectionError,
  onHelp,
}: BrandHeaderProps) {
  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center justify-between border-b border-border bg-card px-4">
      {/* Left: mark + name + section */}
      <div className="flex items-center gap-3 min-w-0">
        <SynvyaMark variant="green" size={26} title="Synvya" />
        <div className="min-w-0">
          <span className="block truncate text-sm font-semibold text-foreground">
            {restaurantName}
          </span>
          <span className="block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {sectionLabel}
          </span>
        </div>
      </div>

      {/* Right: bunker pill + help */}
      <div className="flex items-center gap-2">
        <span title={connectionError ?? undefined}>
          <BunkerPill connected={bunkerConnected} />
        </span>
        <button
          onClick={onHelp}
          className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
          aria-label="Help"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      </div>
    </header>
  )
}
